import { GetStaticPaths, GetStaticProps } from 'next';

import ApiService from '@/lib/api';
import CustomPage from '@/components/CustomPage';
import { CustomRouteWithPage } from '@/types/pages';
import { Namespaces } from '@/types/pages';
import React from 'react';
import { getDraftOrPageContent } from '@/utils/helpers';

export const getStaticPaths: GetStaticPaths = async () => {
  const apiService = new ApiService();
  const postList = await apiService.getCustomPageRoutes(Namespaces.SERVICES);

  return {
    paths: postList
      .filter((route) => route.slug?.current) // Ensure slug is valid
      .map((route) => ({
        params: { slug: route.slug.current.split('/') },
      })),
    // 'blocking' so an unknown slug gets a real 404 on its first request; with
    // `true` Next served the 200 loading shell until the notFound was cached (FOX2-202).
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params, preview = null }) => {
  const apiService = new ApiService();
  const slug = params?.slug ? (Array.isArray(params.slug) ? params.slug.join('/') : params.slug) : '';

  const [data, menuData, { footer }] = await Promise.all([
    apiService.getCustomPageContent(slug, preview, Namespaces.SERVICES),
    apiService.getMenuData(),
    apiService.getMiscData(),
  ]);
  const pageData = getDraftOrPageContent(data);
  if (!pageData?.content) {
    return { notFound: true };
  }
  return {
    props: {
      content: pageData.content,
      isDraft: pageData.isDraft ?? false,
      menuData,
      footer,
      slug: slug || null,
    },
    revalidate: 3600,
  };
};
interface CMSTestProps {
  content: CustomRouteWithPage | null;
  menuData?: unknown;
  footer?: unknown;
  slug?: string[];
  isDraft: boolean | null;
}

const CMSTest: React.FC<CMSTestProps> = (props) => {
  const { content, menuData, footer, slug, isDraft } = props;
  const ogUrl = `https://foxbox.com/services/${slug}`;
  return (
    <CustomPage
      content={content}
      menuData={menuData}
      footer={footer}
      slug={slug}
      isDraft={isDraft}
      ogUrl={ogUrl}
      // The Sanity nav links the individual service pages but never the index,
      // which left /services with no inbound link at all. Every service page
      // now links back to it (FOX2-173).
      sectionLink={{ href: '/services', label: '← All services' }}
    />
  );
};

export default CMSTest;
